import React from 'react';
import { Box, Typography, Checkbox, Button, Paper, Avatar, Stack, Table, TableHead, TableRow, TableCell, TableBody, Switch } from '@mui/material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface EnabledEvents {
  new_project: boolean;
  new_offer: boolean;
  new_subcontractor: boolean;
  new_inquiry?: boolean;
  new_package?: boolean;
  new_pocr?: boolean;
  new_pokr?: boolean;
  new_candidate?: boolean;
  new_contract?: boolean;
}

const NotificationsTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [enabled, setEnabled] = React.useState<EnabledEvents>({ new_project: true, new_offer: true, new_subcontractor: true });
  const [users, setUsers] = React.useState<any[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<string[]>([]);
  const [matrix, setMatrix] = React.useState<Record<string, EnabledEvents>>({});
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes] = await Promise.all([
        api.get('/notifications/settings'),
        api.get('/users')
      ]);
      const data = settingsRes.data?.data;
      const usersData = usersRes.data?.data || [];
      setUsers(usersData);
      if (data) {
        setEnabled({
          new_project: Boolean(data.enabled_events?.new_project),
          new_offer: Boolean(data.enabled_events?.new_offer),
          new_subcontractor: Boolean(data.enabled_events?.new_subcontractor),
          new_contract: Boolean(data.enabled_events?.new_contract)
        });
        setSelectedUserIds(Array.isArray(data.recipient_user_ids) ? data.recipient_user_ids : []);
        setMatrix(data.subscriptions || {});
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { load(); }, []);

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/notifications/settings', {
        enabled_events: enabled,
        recipient_user_ids: selectedUserIds,
        subscriptions: matrix
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof EnabledEvents) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEnabled(prev => ({ ...prev, [key]: e.target.checked }));
  };

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleCell = (userId: string, key: keyof EnabledEvents) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMatrix(prev => ({
      ...prev,
      [userId]: { ...(prev[userId] || { new_project: false, new_offer: false, new_subcontractor: false, new_inquiry: false, new_package: false, new_pocr: false, new_pokr: false, new_candidate: false, new_contract: false }), [key]: e.target.checked }
    }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#004273' }}>Powiadomienia</Typography>
      {/* Usunięto sekcję "Zdarzenia" – pozostaje wyłącznie matryca subskrypcji */}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1">Matryca subskrypcji</Typography>
          <Typography variant="caption" color="text.secondary">Włącz użytkownika przełącznikiem w ostatniej kolumnie</Typography>
        </Stack>
        <Box sx={{ overflowX: 'auto', width: '100%' }}>
          <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              <TableCell>Użytkownik \\ Zdarzenie</TableCell>
              {[
                { key: 'new_project', label: 'Nowy projekt' },
                { key: 'new_offer', label: 'Nowa oferta' },
                { key: 'new_subcontractor', label: 'Nowy podwykonawca' },
                { key: 'new_inquiry', label: 'Nowe zapytanie' },
                { key: 'new_package', label: 'Nowy pakiet' },
                { key: 'new_pocr', label: 'Nowy POCR' },
                { key: 'new_pokr', label: 'Nowy POKR' },
                { key: 'new_candidate', label: 'Nowy kandydat' },
                { key: 'new_contract', label: 'Umowy' },
              ].map(col => (
                <TableCell key={col.key} align="center">{col.label}</TableCell>
              ))}
              <TableCell align="center">Włącz</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.user_id}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={u.avatar_url} sx={{ width: 22, height: 22 }}>
                      {(u.display_name || u.email)?.charAt(0)}
                    </Avatar>
                    <Typography variant="body2">{u.display_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}</Typography>
                  </Stack>
                </TableCell>
                {[
                  'new_project',
                  'new_offer',
                  'new_subcontractor',
                  'new_inquiry',
                  'new_package',
                  'new_pocr',
                  'new_pokr',
                  'new_candidate',
                  'new_contract',
                ].map(key => (
                  <TableCell key={key} align="center">
                    <Checkbox size="small" disabled={!selectedUserIds.includes(u.user_id)} checked={Boolean(matrix[u.user_id]?.[key as keyof EnabledEvents])} onChange={toggleCell(u.user_id, key as keyof EnabledEvents)} />
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Switch size="small" checked={selectedUserIds.includes(u.user_id)} onChange={() => toggleUser(u.user_id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      </Paper>

      <Button variant="contained" onClick={save} disabled={loading}>Zapisz</Button>
    </Box>
  );
};

export default NotificationsTab;


